/** The group: who owns whom, and when — **ADR-0031, gate 16**.
 *
 * `Subsidiary(` was constructed only by the seeder, so a customer could not tell the product that
 * one company owns another, and a subsidiary bought in August could not be told apart from one owned
 * for ever. This is the first screen that can.
 *
 * **Three things here are asked and never defaulted, because each is read as a statement.**
 *
 * - **When the company joined.** An empty start is read by the determination as the parent holding
 *   the company every day of every year, which lets the parent's report stand in for the company's
 *   *own* filing. So the form has no default: a date, or the explicit words *in the group since
 *   before our records*. Neither is pre-selected.
 * - **Whether the parent's report covers it.** `included` decides the same discharge, and it is the
 *   customer's judgement. Neither answer is pre-selected, and *no* needs a reason.
 * - **The consequence, before the button.** Recording or correcting an edge changes what the **next**
 *   determination will say. Determinations already on file are persisted with their own reasoning
 *   and do not move until Readiness is re-determined; saying so after the fact reads as a failed
 *   edit, so it is said in the form and repeated with the count in the result.
 *
 * **More than one level, and more than one ownership period.** An owned company can own another
 * company (ADR-0087). The form excludes the chosen owner and its ancestors from the child list:
 * adding one would create a cycle, regardless of dates. The server checks the complete graph and
 * the periods for overlap; the browser never guesses a return date from a previous period.
 *
 * **Correcting an edge never drops a date.** A start is replaced by another date or by the explicit
 * statement that there is none; an end is cleared only deliberately; and the dates a correction
 * replaces stay in the audit chain, so the screen says they are kept rather than implying they are
 * gone.
 */

import { Link } from "@tanstack/react-router";
import { useState } from "react";

import { ReadFailed } from "../components/Loaded";
import { Empty } from "../components/Panel";
import { useAmendOwnership, useOwnership, useRecordOwnership } from "../api/orgCorrections";
import type { Ownership, OwnershipAmend, OwnershipIn } from "../api/orgCorrections";
import { shortDate } from "../lib/format";
import { keepSearch } from "./search";
import { AddPanel } from "./ScreenParts";
import own from "./Organisation.module.css";

type Report = (result: Promise<unknown>, said: string) => Promise<void>;
type Company = { id: string; name: string };

function cls(...names: Array<string | false | undefined>) {
  return names.filter(Boolean).join(" ");
}

type StartKind = "" | "date" | "before";
type Judgement = "" | "yes" | "no";

function ancestorsOf(owner: string, edges: ReadonlyArray<Ownership>): Set<string> {
  const found = new Set<string>();
  const pending = owner ? [owner] : [];
  while (pending.length) {
    const current = pending.pop();
    if (current === undefined || found.has(current)) continue;
    found.add(current);
    for (const edge of edges) {
      if (edge.legal_entity_id === current) pending.push(edge.parent_entity_id);
    }
  }
  return found;
}

export function GroupSection({
  entities,
  onSubmit,
}: {
  entities: ReadonlyArray<Company>;
  onSubmit: Report;
}) {
  const edges = useOwnership();
  const [correcting, setCorrecting] = useState<string | null>(null);

  if (edges.error) {
    return (
      <ReadFailed
        of="The group's ownership"
        error={edges.error}
        onRetry={() => void edges.refetch()}
      />
    );
  }
  if (edges.isPending) return <Empty>Loading…</Empty>;
  const rows = edges.data ?? [];

  return (
    <>
      {rows.length ? (
        <ul className={cls(own.list)} data-testid="ownership">
          {rows.map((edge) => (
            <li key={edge.id} className={cls(own.row)} data-testid="ownership-row">
              <span className={cls(own.name)}>
                {edge.parent_name} owns {edge.legal_name}
              </span>
              <span className={cls(own.meta)} data-testid="ownership-dates">
                {edge.effective_from
                  ? `in the group from ${shortDate(edge.effective_from)}`
                  : "in the group since before our records"}
                {" · "}
                {edge.effective_to ? `until ${shortDate(edge.effective_to)}` : "still in the group"}
                {" · "}
                {edge.included
                  ? "covered by the parent's report"
                  : `not covered by the parent's report${edge.inclusion_reason ? `: ${edge.inclusion_reason}` : ""}`}
              </span>
              <button
                type="button"
                className={cls(own.inlineButton)}
                data-testid={`correct-ownership-${edge.id}`}
                aria-expanded={correcting === edge.id}
                onClick={() => setCorrecting(correcting === edge.id ? null : edge.id)}
              >
                {correcting === edge.id ? "Hide" : "Correct"}
              </button>
              {correcting === edge.id ? (
                <EdgeCorrection edge={edge} onSubmit={onSubmit} onDone={() => setCorrecting(null)} />
              ) : null}
            </li>
          ))}
        </ul>
      ) : (
        <Empty>
          No ownership recorded. Until it is, every company files for itself and none is treated as
          covered by a parent&rsquo;s report.
        </Empty>
      )}
      <RecordOwnership entities={entities} edges={rows} onSubmit={onSubmit} />
    </>
  );
}

/** The question a dated group change asks (CTX-02, ADR-0108). **A task and a question, not a finding.**
 *
 * The server opens it; this says so, with its title, and points at the work queue. It states no size
 * and does not say a reassessment is required — what it says is what the task says, and the task
 * asserts nothing about whether the change matters.
 */
function BaselineQuestions({ titles }: { titles: ReadonlyArray<string> }) {
  if (titles.length === 0) return null;
  return (
    <div className={cls(own.note)} data-testid="ownership-baseline-question">
      {titles.map((title) => (
        <p key={title}>
          <strong>A task was opened for you:</strong> {title}. EmitIQ has not judged whether this
          change matters and has changed no figure, target or filing because of it.{" "}
          <Link to="/tasks" search={keepSearch}>
            Open your work queue
          </Link>
        </p>
      ))}
    </div>
  );
}

function RecordOwnership({
  entities,
  edges,
  onSubmit,
}: {
  entities: ReadonlyArray<Company>;
  edges: ReadonlyArray<Ownership>;
  onSubmit: Report;
}) {
  const record = useRecordOwnership();
  const [parent, setParent] = useState("");
  const [child, setChild] = useState("");
  const [start, setStart] = useState<StartKind>("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [covered, setCovered] = useState<Judgement>("");
  const [reason, setReason] = useState("");
  const [onFile, setOnFile] = useState<number | null>(null);
  const [questions, setQuestions] = useState<string[]>([]);

  // A repeat pair is permitted for a separate period. Only self/ancestor links are excluded;
  // ownership dates and the complete graph remain the server's authoritative checks.
  const ancestors = ancestorsOf(parent, edges);
  const children = entities.filter((entity) => !ancestors.has(entity.id));

  const startOk = start === "before" || (start === "date" && from !== "");
  const ready =
    parent !== "" &&
    children.some((entity) => entity.id === child) &&
    startOk &&
    covered !== "" &&
    (covered === "yes" || reason.trim() !== "") &&
    (to === "" || start !== "date" || to >= from);

  function reset() {
    setParent("");
    setChild("");
    setStart("");
    setFrom("");
    setTo("");
    setCovered("");
    setReason("");
  }

  if (entities.length < 2) {
    return (
      <p className={cls(own.note)} data-testid="ownership-needs-two">
        Ownership needs two companies. Add the other one first.
      </p>
    );
  }

  return (
    <AddPanel label="Record who owns whom" testId="ownership-panel">
    <form
      className={cls(own.form)}
      data-testid="ownership-form"
      onSubmit={(event) => {
        event.preventDefault();
        if (!ready) return;
        const body: OwnershipIn = {
          parent_entity_id: parent,
          legal_entity_id: child,
          effective_from: start === "date" ? from : null,
          held_since_before_records: start === "before",
          effective_to: to === "" ? null : to,
          included: covered === "yes",
          inclusion_reason: reason.trim() === "" ? null : reason.trim(),
        };
        setOnFile(null);
        setQuestions([]);
        void onSubmit(
          record.mutateAsync(body).then((saved) => {
            setOnFile(saved.determinations_on_file);
            setQuestions(saved.baseline_questions ?? []);
            reset();
          }),
          "Ownership recorded.",
        );
      }}
    >
      <label className={cls(own.field)}>
        <span className={cls(own.label)}>The owner</span>
        <select
          className={cls(own.input)}
          value={parent}
          data-testid="ownership-parent"
          onChange={(event) => {
            setParent(event.target.value);
            if (ancestorsOf(event.target.value, edges).has(child)) setChild("");
          }}
        >
          <option value="">Choose&hellip;</option>
          {entities.map((entity) => (
            <option key={entity.id} value={entity.id}>
              {entity.name}
            </option>
          ))}
        </select>
      </label>
      <label className={cls(own.field)}>
        <span className={cls(own.label)}>The company it owns</span>
        <select
          className={cls(own.input)}
          value={child}
          data-testid="ownership-child"
          onChange={(event) => setChild(event.target.value)}
        >
          <option value="">Choose&hellip;</option>
          {children.map((entity) => (
            <option key={entity.id} value={entity.id}>
              {entity.name}
            </option>
          ))}
        </select>
        <span className={cls(own.hint)} data-testid="ownership-period-guidance">
          An owned company can own another company. A company cannot own itself or an ancestor.
          If it left and returned, record a separate period; periods for the same owner and company
          must not overlap. EmitIQ checks the dates and the whole group before saving.
        </span>
      </label>

      <fieldset className={cls(own.field)}>
        <legend className={cls(own.label)}>When did it join the group?</legend>
        <label className={cls(own.check)}>
          <input
            type="radio"
            name="ownership-start"
            checked={start === "date"}
            data-testid="ownership-start-date"
            onChange={() => setStart("date")}
          />
          <span>It joined on a date</span>
        </label>
        {start === "date" ? (
          <input
            type="date"
            className={cls(own.input)}
            value={from}
            aria-label="First day in the group"
            data-testid="ownership-from"
            onChange={(event) => setFrom(event.target.value)}
          />
        ) : null}
        <label className={cls(own.check)}>
          <input
            type="radio"
            name="ownership-start"
            checked={start === "before"}
            data-testid="ownership-start-before"
            onChange={() => setStart("before")}
          />
          <span>
            It has been in the group since before our records begin
            <span className={cls(own.hint)}>
              This says the parent held it every day of every year. Choose it only if that is true.
            </span>
          </span>
        </label>
      </fieldset>

      <label className={cls(own.field)}>
        <span className={cls(own.label)}>Last day in the group, if it has left</span>
        <input
          type="date"
          className={cls(own.input)}
          value={to}
          data-testid="ownership-to"
          onChange={(event) => setTo(event.target.value)}
        />
        <span className={cls(own.hint)}>Leave it empty while the company is still owned.</span>
      </label>

      <fieldset className={cls(own.field)}>
        <legend className={cls(own.label)}>Does the parent&rsquo;s report cover this company?</legend>
        <label className={cls(own.check)}>
          <input
            type="radio"
            name="ownership-covered"
            checked={covered === "yes"}
            data-testid="ownership-covered-yes"
            onChange={() => setCovered("yes")}
          />
          <span>Yes, the parent reports for it</span>
        </label>
        <label className={cls(own.check)}>
          <input
            type="radio"
            name="ownership-covered"
            checked={covered === "no"}
            data-testid="ownership-covered-no"
            onChange={() => setCovered("no")}
          />
          <span>No, it files for itself</span>
        </label>
        {covered === "no" ? (
          <textarea
            className={cls(own.input)}
            value={reason}
            rows={2}
            maxLength={500}
            aria-label="Why is it not covered?"
            data-testid="ownership-reason"
            onChange={(event) => setReason(event.target.value)}
          />
        ) : null}
      </fieldset>

      <p className={cls(own.note)} data-testid="ownership-moves-filing">
        <strong>This can change which filings a company owes.</strong> Where the parent held the
        company for a whole year and its report covers it, the company&rsquo;s own filing is marked as
        covered. Determinations already on file are not changed; the next time Readiness is
        determined it will read this.
      </p>

      <span className={cls(own.buttons)}>
        <button
          type="submit"
          className={cls(own.submit)}
          disabled={!ready || record.isPending}
          data-testid="ownership-submit"
        >
          {record.isPending ? "Saving…" : "Record ownership"}
        </button>
      </span>
      {onFile !== null && onFile > 0 ? (
        <p className={cls(own.note)} data-testid="ownership-recorded-determinations">
          {onFile} determination{onFile === 1 ? "" : "s"} on file for these two companies{" "}
          {onFile === 1 ? "is" : "are"} unchanged. Determine Readiness again for them to read this.
        </p>
      ) : null}
      <BaselineQuestions titles={questions} />
    </form>
    </AddPanel>
  );
}

function EdgeCorrection({
  edge,
  onSubmit,
  onDone,
}: {
  edge: Ownership;
  onSubmit: Report;
  onDone: () => void;
}) {
  const amend = useAmendOwnership();
  const [start, setStart] = useState<Exclude<StartKind, "">>(edge.effective_from ? "date" : "before");
  const [from, setFrom] = useState(edge.effective_from ?? "");
  const [to, setTo] = useState(edge.effective_to ?? "");
  const [covered, setCovered] = useState<Exclude<Judgement, "">>(edge.included ? "yes" : "no");
  const [reason, setReason] = useState(edge.inclusion_reason ?? "");
  const [onFile, setOnFile] = useState<number | null>(null);
  const [questions, setQuestions] = useState<string[]>([]);

  // **Only what differs.** A start moves to another date, or to the explicit statement that there
  // is none, and never to nothing; an end clears only on a deliberate `null`.
  const body: OwnershipAmend = {};
  if (start === "date" && from !== "" && from !== (edge.effective_from ?? "")) {
    body.effective_from = from;
  }
  if (start === "before" && edge.effective_from !== null) body.held_since_before_records = true;
  if (to !== (edge.effective_to ?? "")) body.effective_to = to === "" ? null : to;
  if ((covered === "yes") !== edge.included) body.included = covered === "yes";
  if (reason.trim() !== (edge.inclusion_reason ?? "")) {
    body.inclusion_reason = reason.trim() === "" ? null : reason.trim();
  }
  const dirty = Object.keys(body).length > 0;
  const ready =
    dirty &&
    (start === "before" || from !== "") &&
    (covered === "yes" || reason.trim() !== "") &&
    (to === "" || start !== "date" || to >= from);

  return (
    <form
      className={cls(own.form)}
      data-testid="ownership-correction-form"
      onSubmit={(event) => {
        event.preventDefault();
        if (!ready) return;
        setQuestions([]);
        void onSubmit(
          amend.mutateAsync({ ownershipId: edge.id, ...body }).then((saved) => {
            setOnFile(saved.determinations_on_file);
            setQuestions(saved.baseline_questions ?? []);
          }),
          `${edge.legal_name}: ownership corrected.`,
        );
      }}
    >
      <fieldset className={cls(own.field)}>
        <legend className={cls(own.label)}>First day in the group</legend>
        <label className={cls(own.check)}>
          <input
            type="radio"
            name={`start-${edge.id}`}
            checked={start === "date"}
            data-testid="correct-ownership-start-date"
            onChange={() => setStart("date")}
          />
          <span>A date</span>
        </label>
        {start === "date" ? (
          <input
            type="date"
            className={cls(own.input)}
            value={from}
            aria-label="First day in the group"
            data-testid="correct-ownership-from"
            onChange={(event) => setFrom(event.target.value)}
          />
        ) : null}
        <label className={cls(own.check)}>
          <input
            type="radio"
            name={`start-${edge.id}`}
            checked={start === "before"}
            data-testid="correct-ownership-start-before"
            onChange={() => setStart("before")}
          />
          <span>Since before our records begin</span>
        </label>
      </fieldset>
      <label className={cls(own.field)}>
        <span className={cls(own.label)}>Last day in the group</span>
        <input
          type="date"
          className={cls(own.input)}
          value={to}
          data-testid="correct-ownership-to"
          onChange={(event) => setTo(event.target.value)}
        />
        <span className={cls(own.hint)}>Empty means the company is still owned.</span>
      </label>
      <fieldset className={cls(own.field)}>
        <legend className={cls(own.label)}>Does the parent&rsquo;s report cover it?</legend>
        <label className={cls(own.check)}>
          <input
            type="radio"
            name={`covered-${edge.id}`}
            checked={covered === "yes"}
            data-testid="correct-ownership-covered-yes"
            onChange={() => setCovered("yes")}
          />
          <span>Yes</span>
        </label>
        <label className={cls(own.check)}>
          <input
            type="radio"
            name={`covered-${edge.id}`}
            checked={covered === "no"}
            data-testid="correct-ownership-covered-no"
            onChange={() => setCovered("no")}
          />
          <span>No</span>
        </label>
        {covered === "no" ? (
          <textarea
            className={cls(own.input)}
            value={reason}
            rows={2}
            maxLength={500}
            aria-label="Why is it not covered?"
            data-testid="correct-ownership-reason"
            onChange={(event) => setReason(event.target.value)}
          />
        ) : null}
      </fieldset>
      <p className={cls(own.note)} data-testid="ownership-dates-are-kept">
        The dates this replaces stay in the audit chain beside the new ones. Determinations already
        on file are not changed until Readiness is determined again.
      </p>
      <span className={cls(own.buttons)}>
        <button
          type="submit"
          className={cls(own.submit)}
          disabled={!ready || amend.isPending}
          data-testid="correct-ownership-submit"
        >
          {amend.isPending ? "Saving…" : "Save the correction"}
        </button>
        <button
          type="button"
          className={cls(own.cancel)}
          data-testid="correct-ownership-cancel"
          onClick={onDone}
        >
          {onFile !== null ? "Close" : "Cancel"}
        </button>
      </span>
      {onFile !== null && onFile > 0 ? (
        <p className={cls(own.note)} data-testid="ownership-determinations-on-file">
          {onFile} determination{onFile === 1 ? "" : "s"} on file for these two companies{" "}
          {onFile === 1 ? "is" : "are"} unchanged. Determine Readiness again for them to read this.
        </p>
      ) : null}
      <BaselineQuestions titles={questions} />
    </form>
  );
}
